import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import SignatureCanvas from 'react-signature-canvas';
import { api } from '../../services/api';
import { useAuthStore } from '../../store/authStore';

// --- Types ---

interface Signature {
  user_id: number;
  user_name: string;
  signed_at: string;
  ip_address: string;
}

interface ContractDetail {
  id: number;
  property_id: number;
  property_title: string;
  tenant_user_id: number;
  tenant_name: string | null;
  company_name: string | null;
  contract_type: string;
  status: string;
  monthly_amount: number;
  currency: string;
  deposit_amount: number;
  start_date: string;
  end_date: string;
  terms: string;
  document_hash: string | null;
  signatures: Signature[];
  created_at: string;
}

interface ContractResponse {
  data: ContractDetail;
}

// --- Constants ---

const CONTRACT_TYPE_LABEL: Record<string, string> = {
  rent: 'Alquiler',
  sale: 'Venta',
  reservation: 'Reserva',
};

const STATUS_LABEL: Record<string, string> = {
  draft: 'Borrador',
  pending_signature: 'Pendiente firma',
  active: 'Activo',
  expired: 'Vencido',
  cancelled: 'Cancelado',
};

const STATUS_BADGE: Record<string, string> = {
  draft: 'badge-ghost',
  pending_signature: 'badge-warning',
  active: 'badge-success',
  expired: 'badge-error',
  cancelled: 'badge-neutral',
};

// --- Component ---

export default function ContractSignPage(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  const sigCanvasRef = useRef<SignatureCanvas>(null);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Auto-dismiss snackbar
  useEffect(() => {
    if (snackbar.open) {
      const timer = setTimeout(() => setSnackbar((s) => ({ ...s, open: false })), 4000);
      return () => clearTimeout(timer);
    }
  }, [snackbar.open]);

  // Fetch contract detail
  const { data: contractData, isLoading } = useQuery({
    queryKey: ['contract', id],
    queryFn: () => api.get<ContractResponse>(`/v1/contracts/${id}`),
    enabled: !!id,
  });

  const contract = contractData?.data;

  // Sign mutation
  const signMutation = useMutation({
    mutationFn: (signatureData: string) =>
      api.post(`/v1/contracts/${id}/sign`, { signature_data: signatureData }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract', id] });
      setSnackbar({ open: true, message: 'Contrato firmado exitosamente', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Error al firmar el contrato', severity: 'error' });
    },
  });

  const handleClear = (): void => {
    sigCanvasRef.current?.clear();
  };

  const handleSign = (): void => {
    if (!sigCanvasRef.current || sigCanvasRef.current.isEmpty()) {
      setSnackbar({ open: true, message: 'Por favor, dibuja tu firma antes de confirmar', severity: 'error' });
      return;
    }
    const signatureData = sigCanvasRef.current.toDataURL('image/png');
    signMutation.mutate(signatureData);
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatPrice = (amount: number, currency: string): string => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: currency || 'ARS',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const hasUserSigned = contract?.signatures?.some((s) => s.user_id === user?.id) ?? false;
  const isPendingSignature = contract?.status === 'pending_signature';
  const isFullySigned = contract?.status === 'active';
  const canSign = isPendingSignature && !hasUserSigned;

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="text-center py-16">
        <p className="text-lg text-base-content/60">Contrato no encontrado</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button className="btn btn-ghost" onClick={() => navigate('/app/contracts')}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Volver
        </button>
        <div>
          <h1 className="text-3xl font-bold">Contrato #{contract.id}</h1>
          <span className={`badge ${STATUS_BADGE[contract.status] || 'badge-ghost'}`}>
            {STATUS_LABEL[contract.status] || contract.status}
          </span>
        </div>
      </div>

      {/* Success banner when fully signed */}
      {isFullySigned && (
        <div className="alert alert-success mb-6">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Contrato firmado por todas las partes. El contrato esta activo.</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Contract details */}
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body">
            <h2 className="card-title text-lg mb-4">Detalles del contrato</h2>

            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-base-content/60">Propiedad</span>
                <span className="font-medium">{contract.property_title}</span>
              </div>
              <div className="divider my-0" />

              <div className="flex justify-between">
                <span className="text-base-content/60">Inquilino</span>
                <span className="font-medium">{contract.tenant_name || '-'}</span>
              </div>
              <div className="divider my-0" />

              <div className="flex justify-between">
                <span className="text-base-content/60">Inmobiliaria</span>
                <span className="font-medium">{contract.company_name || '-'}</span>
              </div>
              <div className="divider my-0" />

              <div className="flex justify-between">
                <span className="text-base-content/60">Tipo</span>
                <span className="font-medium">
                  {CONTRACT_TYPE_LABEL[contract.contract_type] || contract.contract_type}
                </span>
              </div>
              <div className="divider my-0" />

              <div className="flex justify-between">
                <span className="text-base-content/60">Fecha de inicio</span>
                <span className="font-medium">{formatDate(contract.start_date)}</span>
              </div>
              <div className="divider my-0" />

              <div className="flex justify-between">
                <span className="text-base-content/60">Fecha de fin</span>
                <span className="font-medium">{formatDate(contract.end_date)}</span>
              </div>
              <div className="divider my-0" />

              <div className="flex justify-between">
                <span className="text-base-content/60">Monto mensual</span>
                <span className="font-medium">{formatPrice(contract.monthly_amount, contract.currency)}</span>
              </div>
              <div className="divider my-0" />

              <div className="flex justify-between">
                <span className="text-base-content/60">Deposito</span>
                <span className="font-medium">{formatPrice(contract.deposit_amount, contract.currency)}</span>
              </div>

              {contract.document_hash && (
                <>
                  <div className="divider my-0" />
                  <div>
                    <span className="text-base-content/60 text-sm">Hash del documento (SHA-256)</span>
                    <p className="font-mono text-xs break-all mt-1 bg-base-200 p-2 rounded">
                      {contract.document_hash}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Signatures section */}
        <div className="space-y-6">
          {/* Who has signed */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body">
              <h2 className="card-title text-lg mb-4">Estado de firmas</h2>

              {contract.signatures && contract.signatures.length > 0 ? (
                <div className="space-y-3">
                  {contract.signatures.map((sig, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-success/10 rounded-lg">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-success flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <p className="font-medium text-sm">{sig.user_name}</p>
                        <p className="text-xs text-base-content/60">
                          Firmado el {formatDate(sig.signed_at)} - IP: {sig.ip_address}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-base-content/60 text-sm">Aun no hay firmas registradas</p>
              )}

              {isPendingSignature && !hasUserSigned && (
                <div className="flex items-center gap-3 p-3 bg-warning/10 rounded-lg mt-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-warning flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm font-medium">Tu firma esta pendiente</p>
                </div>
              )}

              {hasUserSigned && isPendingSignature && (
                <div className="flex items-center gap-3 p-3 bg-info/10 rounded-lg mt-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-info flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm font-medium">Ya firmaste. Esperando la firma de la otra parte.</p>
                </div>
              )}
            </div>
          </div>

          {/* Signature canvas */}
          {canSign && (
            <div className="card bg-base-100 shadow-sm">
              <div className="card-body">
                <h2 className="card-title text-lg mb-4">Firmar contrato</h2>
                <p className="text-sm text-base-content/60 mb-4">
                  Dibuja tu firma en el recuadro de abajo. La firma se capturara junto con tu IP y la fecha/hora actual.
                </p>

                <div className="border-2 border-dashed border-base-300 rounded-lg bg-white">
                  <SignatureCanvas
                    ref={sigCanvasRef}
                    canvasProps={{
                      className: 'w-full h-48',
                      style: { width: '100%', height: '192px' },
                    }}
                    backgroundColor="white"
                  />
                </div>

                <div className="flex gap-2 mt-4">
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={handleClear}
                  >
                    Limpiar
                  </button>
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    onClick={handleSign}
                    disabled={signMutation.isPending}
                  >
                    {signMutation.isPending ? (
                      <span className="loading loading-spinner loading-sm" />
                    ) : (
                      'Firmar'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Terms section */}
      {contract.terms && (
        <div className="card bg-base-100 shadow-sm mt-6">
          <div className="card-body">
            <h2 className="card-title text-lg mb-4">Terminos y condiciones</h2>
            <div
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: contract.terms }}
            />
          </div>
        </div>
      )}

      {/* Toast */}
      {snackbar.open && (
        <div className="toast toast-end toast-bottom z-50">
          <div className={`alert ${snackbar.severity === 'success' ? 'alert-success' : 'alert-error'}`}>
            <span>{snackbar.message}</span>
            <button className="btn btn-ghost btn-xs" onClick={() => setSnackbar((s) => ({ ...s, open: false }))}>
              &#10005;
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
